import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerRosterMemberRoute,
  leagueCustomerRosterMemberUserSearchRoute,
} from "network/api-routes";
import { playerInitials } from "components/pages/Gestion/player-form-helpers";

const playerDisplayName = (player) =>
  [player?.first_name, player?.last_name].filter(Boolean).join(" ") || "Jugador";

/**
 * Search a Reva mobile user by phone/document and link/unlink to a roster member.
 */
const GestionPlayerUserLinkDialog = ({ open, onClose, customerId, player, onUpdated }) => {
  const { user: authUser } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [searchPhone, setSearchPhone] = useState("");
  const [searchDoc, setSearchDoc] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchEmpty, setSearchEmpty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const linkedUser = player?.linked_user || null;
  const isLinked = Boolean(player?.user_id || player?.is_associated_to_user || linkedUser);

  useEffect(() => {
    if (!open) return;
    setSearchPhone(player?.phone_number || "");
    setSearchDoc(player?.document_number || "");
    setSearchResult(null);
    setSearchEmpty(false);
    setConfirmUnlink(false);
  }, [open, player]);

  const searchUser = () => {
    if (!searchPhone && !searchDoc) {
      enqueueSnackbar(intl.formatMessage({ id: "gestion.players.linkNeedCriteria" }), { variant: "warning" });
      return;
    }
    if (!customerId || !player?.id) return;

    setSearching(true);
    setSearchResult(null);
    setSearchEmpty(false);

    performRequest(
      "POST",
      leagueCustomerRosterMemberUserSearchRoute(customerId, player.id),
      { phone_number: searchPhone, document_number: searchDoc },
      true,
      authUser,
    )
      .then((response) => {
        setSearchResult(response.data?.data || null);
        setSearchEmpty(!response.data?.data);
      })
      .catch((error) => {
        setSearchResult(null);
        if (error?.response?.status === 404) {
          setSearchEmpty(true);
        } else {
          enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
            variant: "error",
          });
        }
      })
      .finally(() => setSearching(false));
  };

  const linkUser = () => {
    if (!customerId || !player?.id || !searchResult?.id) return;

    setSaving(true);
    performRequest(
      "PATCH",
      leagueCustomerRosterMemberRoute(customerId, player.id),
      { user_id: searchResult.id },
      true,
      authUser,
    )
      .then((response) => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.players.userLinked" }), { variant: "success" });
        onUpdated?.(response.data);
        onClose();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        });
      })
      .finally(() => setSaving(false));
  };

  const unlinkUser = () => {
    if (!customerId || !player?.id) return;

    setSaving(true);
    performRequest(
      "PATCH",
      leagueCustomerRosterMemberRoute(customerId, player.id),
      { user_id: null },
      true,
      authUser,
    )
      .then((response) => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.players.userUnlinked" }), { variant: "success" });
        setConfirmUnlink(false);
        onUpdated?.(response.data);
        onClose();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {intl.formatMessage({ id: "gestion.players.linkUserTitle" }, { name: playerDisplayName(player) })}
      </DialogTitle>
      <DialogContent dividers>
        {isLinked ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={linkedUser?.full_profile_pic_url || undefined} sx={{ width: 48, height: 48 }}>
                {playerInitials({ first_name: linkedUser?.full_name || player?.first_name, last_name: player?.last_name })}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RevaText sx={{ fontWeight: 600 }}>{linkedUser?.full_name || intl.formatMessage({ id: "gestion.players.associatedToUser" })}</RevaText>
                {linkedUser?.cellphone_number && (
                  <RevaText variant="body2" color="text.secondary">
                    {linkedUser.cellphone_number}
                  </RevaText>
                )}
                {linkedUser?.document_number && (
                  <RevaText variant="body2" color="text.secondary">
                    {linkedUser.document_number}
                  </RevaText>
                )}
              </Box>
            </Stack>

            <Collapse in={!confirmUnlink}>
              <Button
                color="warning"
                variant="outlined"
                startIcon={<LinkOffIcon />}
                onClick={() => setConfirmUnlink(true)}
                disabled={saving}
              >
                {intl.formatMessage({ id: "gestion.players.unlinkUser" })}
              </Button>
            </Collapse>

            <Collapse in={confirmUnlink}>
              <Alert
                severity="warning"
                action={
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => setConfirmUnlink(false)} disabled={saving}>
                      {intl.formatMessage({ id: "Cancelar" })}
                    </Button>
                    <Button size="small" color="warning" variant="contained" onClick={unlinkUser} disabled={saving}>
                      {intl.formatMessage({ id: "gestion.players.confirmUnlink" })}
                    </Button>
                  </Stack>
                }
              >
                {intl.formatMessage({ id: "gestion.players.unlinkConfirm" })}
              </Alert>
            </Collapse>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <RevaText variant="body2" color="text.secondary">
              {intl.formatMessage({ id: "gestion.players.linkUserHint" })}
            </RevaText>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: "Teléfono" })}
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUser()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={searchUser} disabled={searching}>
                        {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: "Documento de identidad" })}
                value={searchDoc}
                onChange={(e) => setSearchDoc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUser()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={searchUser} disabled={searching}>
                        {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {searchEmpty && (
              <Alert severity="info">{intl.formatMessage({ id: "gestion.players.userNotFound" })}</Alert>
            )}

            {searchResult && (
              <>
                <Divider />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={searchResult.full_profile_pic_url} />
                  <RevaText sx={{ flex: 1 }}>{searchResult.full_name}</RevaText>
                  <Button variant="contained" onClick={linkUser} disabled={saving}>
                    {intl.formatMessage({ id: "Vincular usuario" })}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{intl.formatMessage({ id: "Cancelar" })}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GestionPlayerUserLinkDialog;
